import { FadeUp, Eyebrow } from "./Reveal";
import { CatMark } from "./Doodles";
import WaveField from "./Waves";
import { Linkedin } from "lucide-react";
import { useLang } from "@/i18n";

// Fill these in when ready: { name: "Full Name", role: "Role", photo: "/assets/team-x.jpg", linkedin: "https://..." }
const MEMBERS = [
  { name: "Abhishek Kharbikar", role: "Business Strategy Analyst", photo: "/assets/team-abhishek.jpg", linkedin: "https://www.linkedin.com/in/abhishek-kharbikar-007520176/" },
  { name: "Archana Thammala", role: "Digital Creator / Marketing Associate", photo: "/assets/team-archana.jpg", linkedin: "https://www.linkedin.com/in/archana-thammala/" },
  { name: "Jagruti Kukadiya", role: "UI/UX Developer", photo: "/assets/team-jagruti.jpg", linkedin: "https://www.linkedin.com/in/webblor/" },
  { name: "Kunal Narwara", role: "Influencer Marketing Manager", photo: "/assets/team-kunal.jpg", linkedin: "https://www.linkedin.com/in/narwarakunal/" },
  { name: "Nareish Lachman", role: "Sales Associate", photo: "/assets/team-nareish.jpg", linkedin: "https://www.linkedin.com/in/nareish-lachman-8a577797/" },
];

export default function Team() {
  const { t } = useLang();
  return (
    <section id="team" data-testid="team-section" className="relative py-24 sm:py-36 bg-[#2B0A4E] overflow-hidden border-y-4 border-[#F2C230]">
      <WaveField variant="dark" className="absolute -top-24 -right-16 w-[520px] sm:w-[680px] opacity-20 pointer-events-none rotate-180" />
      <WaveField variant="dark" className="absolute -bottom-28 -left-16 w-[460px] opacity-15 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <FadeUp className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="font-mono-label text-xs tracking-[0.25em] uppercase text-[#F2C230]">{t("team_eyebrow")}</p>
            <h2 className="mt-4 font-serif-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#FDFBF5] leading-tight" data-testid="team-heading">
              {t("team_h1")} <span className="italic text-[#F2C230]">{t("team_h2")}</span>
            </h2>
          </div>
          <p className="font-mono-label text-[11px] tracking-[0.2em] uppercase text-[#FDFBF5]/50">
            {t("team_note")}
          </p>
        </FadeUp>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 sm:gap-8">
          {MEMBERS.map((m, i) => (
            <FadeUp key={i} delay={0.08 * i}>
              <div data-testid={`team-member-${i + 1}`} className="group text-center">
                <div className="relative aspect-[4/5] rounded-2xl border-2 border-dashed border-[#F2C230]/70 bg-[#FFFFFF] flex items-center justify-center overflow-hidden group-hover:-translate-y-1.5 group-hover:shadow-[6px_6px_0_#F2C230] transition-[transform,box-shadow] duration-300">
                  {m.photo ? (
                    <img src={m.photo} alt={m.name} className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <CatMark className="w-1/2 opacity-[0.08] group-hover:opacity-[0.16] transition-opacity duration-500" />
                  )}
                  <span className="absolute top-3 left-3 font-mono-label text-[9px] tracking-[0.2em] uppercase text-[#2B0A4E]/40">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="mt-4 space-y-2">
                  {m.name ? (
                    <p className="font-serif-display font-semibold text-lg text-[#FDFBF5]">
                      {m.linkedin ? (
                        <a
                          data-testid={`team-linkedin-${i + 1}`}
                          href={m.linkedin}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:text-[#F2C230] transition-colors duration-300 underline-offset-4 hover:underline"
                        >
                          {m.name}
                        </a>
                      ) : (
                        m.name
                      )}
                    </p>
                  ) : (
                    <div className="mx-auto w-3/4 border-b border-dashed border-[#FDFBF5]/40 h-6" aria-label="Name placeholder" />
                  )}
                  {m.role ? (
                    <p className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#F2C230]">{m.role}</p>
                  ) : (
                    <div className="mx-auto w-1/2 border-b border-dashed border-[#FDFBF5]/25 h-4" aria-label="Role placeholder" />
                  )}
                  {m.linkedin && (
                    <a
                      data-testid={`team-linkedin-icon-${i + 1}`}
                      href={m.linkedin}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`${m.name} on LinkedIn`}
                      className="inline-flex items-center gap-1.5 font-mono-label text-[9px] tracking-[0.2em] uppercase text-[#FDFBF5]/60 hover:text-[#F2C230] transition-colors duration-300"
                    >
                      <Linkedin size={13} /> LinkedIn
                    </a>
                  )}
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
